import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import GitHubButton from "react-github-btn";
import './style.scss';

const typeColors = {
    normal: "164, 172, 175",
    fire: "253, 125, 36",
    water: "69, 146, 196",
    grass: "155, 204, 80",
    flying: "61, 199, 239",
    fighting: "213, 103, 35",
    poison: "185, 127, 201",
    electric: "238, 213, 53",
    ground: "247, 222, 63",
    rock: "163, 140, 33",
    psychic: "243, 102, 185",
    ice: "81, 196, 231",
    bug: "114, 159, 63",
    ghost: "123, 98, 163",
    steel: "158, 183, 184",
    dragon: "83, 164, 207",
    dark: "112, 112, 112",
    fairy: "253, 185, 233"
};

const delayAndUpdateCurrentNumber = async (delay) => {
    await new Promise(resolve => setTimeout(resolve, delay));
};

// Pokemon Image.
function Appearance(props) {
    const [displayed, setDisplayed] = useState({
        number: props.number,
        isFront: props.isFront,
        isShiny: props.isShiny
    });
    const [outgoing, setOutgoing] = useState(null);
    const [transitionType, setTransitionType] = useState('shuffle'); // 'shuffle' or 'fade'
    const prevRef = useRef({
        number: props.number,
        isFront: props.isFront,
        isShiny: props.isShiny
    });

    useEffect(() => {
        const prev = prevRef.current;
        const numberChanged = prev.number !== props.number;
        const spriteChanged = prev.isFront !== props.isFront || prev.isShiny !== props.isShiny;

        if (numberChanged || spriteChanged) {
            setTransitionType(numberChanged ? 'shuffle' : 'fade');
            setOutgoing(prev);
            setDisplayed({
                number: props.number,
                isFront: props.isFront,
                isShiny: props.isShiny
            });
            prevRef.current = {
                number: props.number,
                isFront: props.isFront,
                isShiny: props.isShiny
            };

            const timeout = setTimeout(() => {
                setOutgoing(null);
            }, 400);

            return () => clearTimeout(timeout);
        }
    }, [props.number, props.isFront, props.isShiny]);

    const getImageUrl = (state) => state.isShiny
        ? (state.isFront
            ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/shiny/${state.number}.png`
            : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/back/shiny/${state.number}.png`)
        : (state.isFront
            ? `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${state.number}.png`
            : `https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/back/${state.number}.png`);

    const outClass = transitionType === 'shuffle' ? 'shuffleOut' : 'fadeOut';
    const inClass = transitionType === 'shuffle' ? 'shuffleIn' : 'fadeIn';

    return (
        <div className="pokemonFloat">
            {outgoing && (
                <img
                    key={`out-${outgoing.number}-${outgoing.isFront}-${outgoing.isShiny}`}
                    src={getImageUrl(outgoing)}
                    alt={`Pokemon ${outgoing.number}`}
                    className={`pokemonImage ${outClass}`}
                />
            )}
            <img
                key={`in-${displayed.number}-${displayed.isFront}-${displayed.isShiny}`}
                src={getImageUrl(displayed)}
                alt={`Pokemon ${displayed.number}`}
                className={`pokemonImage ${outgoing ? inClass : ''}`}
            />
        </div>
    );
}

function capitalizeName(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

function formatWords(str) {
    return str.split(/[-\s]+/).map(capitalizeName).join(' ');
}

function describeEvolutionDetail(d) {
    const parts = [];

    switch (d.trigger?.name) {
        case 'level-up':
            parts.push(d.min_level ? `Level ${d.min_level}` : 'Level up');
            if (d.min_happiness) parts.push(`Happiness ${d.min_happiness}+`);
            if (d.min_beauty) parts.push(`Beauty ${d.min_beauty}+`);
            if (d.min_affection) parts.push(`Affection ${d.min_affection}+`);
            if (d.time_of_day) parts.push(formatWords(d.time_of_day));
            if (d.known_move) parts.push(`Knows ${formatWords(d.known_move.name)}`);
            if (d.known_move_type) parts.push(`${formatWords(d.known_move_type.name)} move`);
            if (d.location) parts.push(`At ${formatWords(d.location.name)}`);
            if (d.held_item) parts.push(`Holding ${formatWords(d.held_item.name)}`);
            if (d.relative_physical_stats === 1) parts.push('Atk > Def');
            else if (d.relative_physical_stats === -1) parts.push('Def > Atk');
            else if (d.relative_physical_stats === 0) parts.push('Atk = Def');
            break;
        case 'trade':
            parts.push('Trade');
            if (d.held_item) parts.push(`Holding ${formatWords(d.held_item.name)}`);
            if (d.trade_species) parts.push(`For ${formatWords(d.trade_species.name)}`);
            break;
        case 'use-item':
            if (d.item) parts.push(`Use ${formatWords(d.item.name)}`);
            break;
        case 'shed':
            parts.push('Empty party slot + Poké Ball');
            break;
        case 'three-critical-hits':
            parts.push('Land 3 crits in one battle');
            break;
        case 'take-damage':
            parts.push('Take damage, then level up');
            break;
        case 'other':
            parts.push('Special condition');
            break;
        default:
            if (d.trigger?.name) parts.push(formatWords(d.trigger.name));
    }

    return parts.length ? parts.join(', ') : 'Unknown';
}

function describeEvolution(detailsList) {
    if (!detailsList || detailsList.length === 0) return '';
    return detailsList.map(describeEvolutionDetail).join(' / ');
}

function layoutTree(root) {
    let leafIndex = 0;
    const nodes = [];
    const edges = [];

    const visit = (node, depth) => {
        node._depth = depth;

        if (node.children.length === 0) {
            node._row = leafIndex;
            leafIndex++;
        } else {
            node.children.forEach(child => visit(child, depth + 1));
            const rows = node.children.map(c => c._row);
            node._row = (Math.min(...rows) + Math.max(...rows)) / 2;
        }

        nodes.push(node);
        node.children.forEach(child => edges.push({ from: node, to: child }));
    };

    visit(root, 0);

    return {
        nodes,
        edges,
        maxDepth: Math.max(...nodes.map(n => n._depth)),
        leafCount: leafIndex
    };
}

function useEvolutionChain(number) {
    const [tree, setTree] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const speciesRes = await fetch(`https://pokeapi.co/api/v2/pokemon-species/${number}`);
                const species = await speciesRes.json();
                const chainRes = await fetch(species.evolution_chain.url);
                const chainData = await chainRes.json();

                const parseNode = (node) => ({
                    id: Number(node.species.url.split('/').filter(Boolean).pop()),
                    name: node.species.name,
                    children: node.evolves_to.map(child => ({
                        ...parseNode(child),
                        evolutionDetails: child.evolution_details
                    }))
                });

                if (!cancelled) setTree(parseNode(chainData.chain));
            } catch (error) {
                console.error('Error fetching evolution chain:', error);
                if (!cancelled) setTree(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [number]);

    return { tree, loading };
}

function PokemonHeightWeight(props) {
    const [height, setHeight] = useState('');
    const [weight, setWeight] = useState('');
    const [stats, setStats] = useState([]);
    const [fetchingStats, setFetchingStats] = useState(false);

    const statLabels = {
        hp: 'HP',
        attack: 'Attack',
        defense: 'Defense',
        'special-attack': 'Sp. Attack',
        'special-defense': 'Sp. Defense',
        speed: 'Speed'
    };

    useEffect(() => {
        const fetchHeightWeight = async () => {
            try {
                const response = await fetch(`https://pokeapi.co/api/v2/pokemon/${props.number}`);
                const data = await response.json();

                // Calculate height and weight as before
                const pokemonHeight = data.height * 10;
                const pokemonWeight = data.weight / 10;

                // Update state with fetched height and weight
                setHeight(`${pokemonHeight}cm`);
                setWeight(`${pokemonWeight}kg`);

                // Extract base stats
                const baseStats = data.stats.map(stat => ({
                    name: stat.stat.name,
                    value: stat.base_stat
                }));

                // Update state with base stats
                setStats(baseStats);
            } catch (error) {
                console.error('Error fetching Pokemon height, weight, and stats:', error);
            } finally {
                setFetchingStats(false);
            }
        };

        fetchHeightWeight();
    }, [props.number]);

    return (
        <div className="PokemonHeightWeight">
            <div className="HeightWeight">
                <div className="HeightWeightGrid">
                    <div className="HeightWeightGridItem">
                        <p><strong>Height:</strong> {height}</p>
                    </div>
                    <div className="HeightWeightGridItem">
                        <p><strong>Weight:</strong> {weight}</p>
                    </div>
                </div>
            </div>
            <div className="BaseStats">
                <div className="StatsGrid">
                    {stats.map((stat, index) => (
                        <div key={index} className="StatItem">
                            <span className="BoldLabel">{statLabels[stat.name]}</span>: {stat.value}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function PokemonName(props) {
    const [name, setName] = useState('');
    const [visible, setVisible] = useState(true);
    const timeoutRef = useRef(null);
    const controllerRef = useRef(null);

    useEffect(() => {
        return () => {
            clearTimeout(timeoutRef.current);
            if (controllerRef.current) controllerRef.current.abort();
        };
    }, []);

    useEffect(() => {
        setVisible(false); // fade current name out

        const controller = new AbortController();
        controllerRef.current = controller;

        const fetchData = async () => {
            try {
                const response = await fetch(`https://pokeapi.co/api/v2/pokemon/${props.number}`, { signal: controller.signal });
                const data = await response.json();

                let pokemonName = data.name
                    .split('-')
                    .map(capitalizeName)
                    .join(' ');

                if (data.name === 'nidoran-m') {
                    pokemonName = 'Nidoran \u2642';
                } else if (data.name === 'nidoran-f') {
                    pokemonName = 'Nidoran \u2640';
                }

                const formattedName = pokemonName
                    .replace(/\f/g, '\n')
                    .replace(/\u00ad\n/g, '')
                    .replace(/\u00ad/g, '')
                    .replace(/ -\n/g, ' - ')
                    .replace(/-\n/g, '-')
                    .replace(/\n/g, ' ');

                setName(formattedName);
                setVisible(true); // fade new name in
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching Pokemon Name:', error);
                }
            }
        };

        timeoutRef.current = setTimeout(fetchData, 150);

        return () => {
            clearTimeout(timeoutRef.current);
            controller.abort();
        };
    }, [props.number]);

    return (
        <p className={visible ? 'textFadeIn' : 'textFadeOut'}>{name}</p>
    );
}
function Sidebar({
    cardColor,
    setCurrentRoute,
    setSelectedType,
    currentRoute,
    isComparing,
    onToggleComparison
}) {
    const [typeOpen, setTypeOpen] = useState(false);

    useEffect(() => {
        if (currentRoute !== "type" || isComparing) {
            setTypeOpen(false);
        }
    }, [currentRoute, isComparing]);

    const types = [
        "normal", "fire", "water", "grass", "electric", "ice",
        "fighting", "poison", "ground", "flying", "psychic", "bug",
        "rock", "ghost", "dragon", "dark", "steel", "fairy"
    ];

    const activeTool =
        currentRoute === "type"
            ? "type"
            : currentRoute === "evolution"
                ? "evolution"
                : isComparing
                    ? "comparison"
                    : null;

    return (
        <aside
            className="sidebar"
            style={{ background: `rgba(${cardColor}, 0.75)` }}
        >
            <div className="sidebarTop">
                <div className="sidebarHeader">
                    <h2>Tools</h2>
                </div>

                <div className="sidebarItem">
                    <div className="githubButtonWrapper">
                        <GitHubButton
                            href="https://github.com/AjayArora1/Interactive_Pokedex"
                            data-size="large"
                            data-show-count="true"
                            aria-label="Star AjayArora1/Interactive_Pokedex on GitHub"
                        >
                            Star
                        </GitHubButton>
                    </div>
                </div>

                <a
                    href="https://www.paypal.com/donate/?hosted_button_id=KKW24HMCKY2NJ"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="sidebarButton supportButton"
                >
                    ☕ Support the Project
                </a>
            </div>

            <div className="sidebarBottom">
                <button
                    className={`sidebarButton ${activeTool && activeTool !== "type" ? "disabled" : ""}`}
                    disabled={activeTool && activeTool !== "type"}
                    onClick={() => setTypeOpen(!typeOpen)}
                >
                    Type Explorer
                    <span>{typeOpen ? "▲" : "▼"}</span>
                </button>

                {typeOpen && (
                    <div className="typeGrid">
                        {types.map(type => (
                            <button
                                key={type}
                                className={`PokemonType ${type}`}
                                onClick={() => {
                                    setSelectedType(type);
                                    setCurrentRoute("type");
                                    window.history.pushState({}, "", `/${type}`);
                                    document.title = `${capitalizeName(type)} Type | Interactive Pokédex`;
                                }}
                            >
                                {type}
                            </button>
                        ))}
                    </div>
                )}

                <button
                    className={`sidebarButton ${activeTool && activeTool !== "evolution" ? "disabled" : ""}`}
                    disabled={activeTool && activeTool !== "evolution"}
                    onClick={() => setCurrentRoute("evolution")}
                >
                    Evolution Tree
                </button>

                <button
                    className="sidebarButton disabled"
                    disabled
                >
                    (Coming Soon)
                </button>

                <button
                    className={`sidebarButton ${activeTool && activeTool !== "comparison" ? "disabled" : ""}`}
                    disabled={activeTool && activeTool !== "comparison"}
                    onClick={onToggleComparison}
                >
                    {isComparing ? "Stop Comparing" : "Comparison"}
                </button>
            </div>
        </aside>
    );
}

function TypeExplorerView({
    type,
    cardColor,
    onSelectPokemon
}) {
    const [pokemon, setPokemon] = useState([]);

    useEffect(() => {
        const fetchType = async () => {
            const res = await fetch(`https://pokeapi.co/api/v2/type/${type}`);
            const data = await res.json();

            setPokemon(
                data.pokemon
                    .filter(p => p.slot === 1)
                    .map(p => ({
                        id: Number(p.pokemon.url.split("/").filter(Boolean).pop()),
                        name: p.pokemon.name
                    }))
                    .filter(p => p.id <= 1025)
            );
        };

        fetchType();
    }, [type]);

    return (
        <div className="TypeExplorerCard">
            <h2>{capitalizeName(type)} Type</h2>

            <div className="TypeExplorerGrid">
                {pokemon.map(p => (
                    <button
                        key={p.id}
                        className="TypePokemonButton"
                        onClick={async () => {
                            const res = await fetch(
                                `https://pokeapi.co/api/v2/pokemon/${p.id}`
                            );
                            const data = await res.json();

                            onSelectPokemon(p.id, data.name);
                        }}
                    >
                        <div className="typePokemonFloat">
                            <img
                                src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${p.id}.png`}
                                alt={p.name}
                            />
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

const EVO_NODE_WIDTH = 100;
const EVO_NODE_HEIGHT = 110;
const EVO_SPRITE_SIZE = 80;
const EVO_COL_WIDTH = 200;
const EVO_ROW_HEIGHT = 130;
const EVO_PADDING = 30;

function EvolutionTreeView({ number, onSelectPokemon, onClose }) {
    const { tree, loading } = useEvolutionChain(number);

    if (loading) {
        return (
            <div className="EvolutionTreeCard">
                <h2>Evolution Tree</h2>
                <p>Loading...</p>
            </div>
        );
    }

    if (!tree) {
        return (
            <div className="EvolutionTreeCard">
                <button className="closeComparison" onClick={onClose}>✕</button>
                <h2>Evolution Tree</h2>
                <p>Unable to load evolution data.</p>
            </div>
        );
    }

    const { nodes, edges, maxDepth, leafCount } = layoutTree(tree);

    const canvasWidth = maxDepth * EVO_COL_WIDTH + EVO_NODE_WIDTH + EVO_PADDING * 2;
    const canvasHeight = (leafCount - 1) * EVO_ROW_HEIGHT + EVO_NODE_HEIGHT + EVO_PADDING * 2;

    const nodePos = (node) => ({
        x: node._depth * EVO_COL_WIDTH + EVO_PADDING,
        y: node._row * EVO_ROW_HEIGHT + EVO_PADDING
    });

    return (
        <div className="EvolutionTreeCard">
            <button className="closeComparison" onClick={onClose}>✕</button>
            <h2>Evolution Tree</h2>

            <div className="EvolutionScrollArea">
                <div
                    className="EvolutionCanvas"
                    style={{ width: canvasWidth, height: canvasHeight }}
                >
                    <svg className="EvolutionSvg" width={canvasWidth} height={canvasHeight}>
                        {edges.map((edge, i) => {
                            const from = nodePos(edge.from);
                            const to = nodePos(edge.to);
                            const x1 = from.x + EVO_NODE_WIDTH;
                            const y1 = from.y + EVO_SPRITE_SIZE / 2;
                            const x2 = to.x;
                            const y2 = to.y + EVO_SPRITE_SIZE / 2;
                            return (
                                <line
                                    key={i}
                                    x1={x1} y1={y1} x2={x2} y2={y2}
                                    className="EvolutionLine"
                                />
                            );
                        })}
                    </svg>

                    {edges.map((edge, i) => {
                        const from = nodePos(edge.from);
                        const to = nodePos(edge.to);
                        const midX = (from.x + EVO_NODE_WIDTH + to.x) / 2;
                        const midY = (from.y + to.y) / 2 + EVO_SPRITE_SIZE / 2;
                        return (
                            <div
                                key={i}
                                className="EvolutionLabel"
                                style={{ left: midX, top: midY }}
                            >
                                {describeEvolution(edge.to.evolutionDetails)}
                            </div>
                        );
                    })}

                    {nodes.map((node) => {
                        const pos = nodePos(node);
                        const isCurrent = node.id === Number(number);
                        return (
                            <button
                                key={node.id}
                                className={`EvolutionNode ${isCurrent ? 'current' : ''}`}
                                style={{ left: pos.x, top: pos.y }}
                                onClick={() => onSelectPokemon(node.id)}
                            >
                                <img
                                    src={`https://raw.githubusercontent.com/PokeAPI/sprites/master/sprites/pokemon/${node.id}.png`}
                                    alt={node.name}
                                />
                                <span className="EvolutionNodeName">{formatWords(node.name)}</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function PokemonBio(props) {
    const [bio, setBio] = useState('');
    const [visible, setVisible] = useState(true);
    const fetchControllerRef = useRef(null);
    const timeoutRef = useRef(null);

    useEffect(() => {
        return () => {
            if (fetchControllerRef.current) fetchControllerRef.current.abort();
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        setVisible(false); // fade current text out

        const controller = new AbortController();
        fetchControllerRef.current = controller;

        const fetchBio = async () => {
            try {
                const response = await fetch(`https://pokeapi.co/api/v2/pokemon-species/${props.number}`, { signal: controller.signal });
                const data = await response.json();

                const englishBioEntry = data.flavor_text_entries.find(entry => entry.language.name === 'en');

                const pokemonBio = englishBioEntry.flavor_text
                    .replace(/\f/g, '\n')
                    .replace(/\u00ad\n/g, '')
                    .replace(/\u00ad/g, '')
                    .replace(/ -\n/g, ' - ')
                    .replace(/-\n/g, '-')
                    .replace(/\n/g, ' ');

                setBio(pokemonBio);
                setVisible(true); // fade new text in
            } catch (error) {
                if (error.name !== 'AbortError') {
                    console.error('Error fetching Pokemon Bio:', error);
                    setBio('Bio not available');
                    setVisible(true);
                }
            }
        };

        // wait for the fade-out (150ms) before swapping content, then debounce the fetch
        timeoutRef.current = setTimeout(fetchBio, 150);

        return () => {
            clearTimeout(timeoutRef.current);
            controller.abort();
        };
    }, [props.number]);

    return (
        <p className={`PokemonBio ${visible ? 'textFadeIn' : 'textFadeOut'}`}>{bio}</p>
    );
}

function PokemonType(props) {
    const [types, setTypes] = useState([]);
    const [hasType, setHasType] = useState(false);

    useEffect(() => {
        const fetchTypes = async () => {
            try {
                const response = await fetch(
                    `https://pokeapi.co/api/v2/pokemon/${props.number}`
                );

                const data = await response.json();

                const pokemonTypes = data.types.map(
                    slot => slot.type.name
                );

                setTypes(pokemonTypes);
                setHasType(pokemonTypes.length > 0);
            } catch (error) {
                console.error('Error fetching Pokemon Types:', error);
            }
        };

        fetchTypes();
    }, [props.number]);

    useEffect(() => {
        if (hasType) {
            const commonType = types.find(type => typeColors[type]);

            if (commonType) {
                props.setCardColor(typeColors[commonType]);
            }
        }
    }, [types, hasType]);

    return (
        <div className="PokemonTypes">
            {types.join(' ').split(' ').map((type, index) => (
                <span
                    key={index}
                    className={`PokemonType ${type}`}
                >
                    {type}
                </span>
            ))}
        </div>
    );
}

function PokedexCard({ id, initialNumber, isPrimary, onPrimaryColorChange, onPrimaryNumberChange, isEntering, isExiting, onClose }) {
    const [currentNumber, setCurrentNumber] = useState(initialNumber);
    const [inputValue, setInputValue] = useState('');
    const [audioSrc, setAudioSrc] = useState('');
    const [isFront, setIsFront] = useState(true);
    const [isShiny, setIsShiny] = useState(false);
    const [isShuffling, setIsShuffling] = useState(false);
    const [cardColor, setCardColor] = useState('255, 255, 255');

    useEffect(() => {
        if (isEntering || isExiting) return; // don't double up with the slide animation
        setIsShuffling(true);
        const timeout = setTimeout(() => setIsShuffling(false), 400);
        return () => clearTimeout(timeout);
    }, [currentNumber, isEntering, isExiting]);

    useEffect(() => {
        if (isPrimary && onPrimaryColorChange) {
            onPrimaryColorChange(cardColor);
        }
    }, [cardColor, isPrimary]);

    useEffect(() => {
        if (isPrimary && onPrimaryNumberChange) {
            onPrimaryNumberChange(currentNumber);
        }
    }, [currentNumber, isPrimary]);

    useEffect(() => {
        const fetchPokemonData = async () => {
            try {
                const response = await fetch(`https://pokeapi.co/api/v2/pokemon/${currentNumber}`);
                const data = await response.json();

                const soundUrl = `https://raw.githubusercontent.com/PokeAPI/cries/main/cries/pokemon/latest/${currentNumber}.ogg`;
                setAudioSrc(soundUrl);

                if (isPrimary) {
                    window.history.replaceState({}, '', `/${data.name}`);
                    document.title = `${capitalizeName(data.name)} #${currentNumber} | Interactive Pokédex`;
                }
            } catch (error) {
                console.error('Error fetching Pokemon:', error);
            }
        };

        fetchPokemonData();
    }, [currentNumber, isPrimary]);

    const playPokemonSound = () => {
        if (audioSrc) {
            const audio = new Audio(audioSrc);
            audio.play();
        }
    };

    const toggleSprite = () => setIsFront(f => !f);
    const toggleShinySprite = () => setIsShiny(s => !s);

    const handleLeftArrowClick = () => {
        setCurrentNumber((prevNumber) => (prevNumber === 1 ? 1025 : prevNumber - 1));
    };

    const handleRightArrowClick = () => {
        setCurrentNumber((prevNumber) => (prevNumber === 1025 ? 1 : prevNumber + 1));
    };

    const handleInputChange = (event) => setInputValue(event.target.value);

    const handleInputSubmit = (event) => {
        if (event.key === 'Enter') {
            const value = parseInt(inputValue);
            if (!isNaN(value) && value >= 1 && value <= 1025) {
                setCurrentNumber(value);
                setInputValue('');
            }
        }
    };

    // Only the primary (first) card responds to global arrow-key navigation,
    // so comparing a second Pokemon doesn't fight with arrow presses.
    useEffect(() => {
        if (!isPrimary) return;

        const handleKeyPress = (event) => {
            if (event.key === "ArrowLeft") handleLeftArrowClick();
            else if (event.key === "ArrowRight") handleRightArrowClick();
        };

        document.addEventListener('keydown', handleKeyPress);
        return () => document.removeEventListener('keydown', handleKeyPress);
    }, [isPrimary]);

    const animationClass = isExiting ? 'comparisonExit' : (isEntering ? 'comparisonEnter' : '');

    const slideClass = isExiting ? 'comparisonExit' : (isEntering ? 'comparisonEnter' : '');
    const shuffleClass = (isShuffling && !isEntering && !isExiting) ? 'cardShuffle' : '';

    return (
        <div
            className={`Pokedex ${shuffleClass} ${slideClass}`}
            style={{ background: `rgba(${cardColor}, 0.75)` }}
        >
            {!isPrimary && (
                <button className="closeComparison" onClick={onClose}>✕</button>
            )}

            <div className="PokedexContent">
                <audio src={audioSrc} id={`pokemonCry-${id}`}></audio>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Appearance
                        number={currentNumber.toString()}
                        isFront={isFront}
                        isShiny={isShiny}
                    />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <h1 className="pokemonTitleContainer">
                        <p>#{currentNumber}</p>
                        <PokemonName number={currentNumber} />

                        <button className="crySound" onClick={playPokemonSound}>&#128266;</button>
                        <button className="toggleSprite" onClick={toggleSprite}>&#8634;</button>
                        <button className="toggleShinySprite" onClick={toggleShinySprite}>&#10024;</button>
                    </h1>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '4px 0 4px 0' }}>
                    <button className="leftArrow" onClick={handleLeftArrowClick}>&#8592;</button>

                    <input
                        type="number"
                        className="lookup"
                        value={inputValue}
                        onChange={handleInputChange}
                        onKeyDown={handleInputSubmit}
                        placeholder="Lookup # (Press Enter)"
                    />

                    <button className="rightArrow" onClick={handleRightArrowClick}>&#8594;</button>
                </div>

                <PokemonBio number={currentNumber} />
                <PokemonHeightWeight number={currentNumber} />

                <h2 className="pokemonTypeContainer">
                    <PokemonType number={currentNumber} setCardColor={setCardColor} />
                </h2>
            </div>
        </div>
    );
}

function App() {
    const [inputValue, setInputValue] = React.useState(''); // can be removed too, unused now
    const [direction, setDirection] = useState(''); // unused, can remove
    const pokemonTypes = [
        "normal", "fire", "water", "grass", "electric", "ice",
        "fighting", "poison", "ground", "flying", "psychic", "bug",
        "rock", "ghost", "dragon", "dark", "steel", "fairy"
    ];

    const [currentRoute, setCurrentRoute] = useState("pokemon");
    const [selectedType, setSelectedType] = useState(null);

    const [initialNumber, setInitialNumber] = useState(1);
    const [primaryNumber, setPrimaryNumber] = useState(1);

    const [isComparing, setIsComparing] = useState(false);
    const [isComparisonClosing, setIsComparisonClosing] = useState(false);
    const [comparisonKey, setComparisonKey] = useState(0);

    const [pokemonSlug, setPokemonSlug] = useState('');

    useEffect(() => {
        const slug = window.location.pathname.replace("/", "").toLowerCase();
        if (!slug) return;

        if (pokemonTypes.includes(slug)) {
            setCurrentRoute("type");
            setSelectedType(slug);
            document.title = `${capitalizeName(slug)} Type | Interactive Pokédex`;
            return;
        }

        const loadPokemon = async () => {
            try {
                const response = await fetch(`https://pokeapi.co/api/v2/pokemon/${slug}`);
                const data = await response.json();
                setInitialNumber(data.id);
                setCurrentRoute("pokemon");
            } catch (err) {
                console.error(err);
            }
        };

        loadPokemon();
    }, []);

    const [cardColor, setCardColor] = useState('255, 255, 255');

    useEffect(() => {
        document.body.style.backgroundColor = `rgba(${cardColor}, 0.25)`;
        return () => { document.body.style.backgroundColor = ''; };
    }, [cardColor]);

    // Reset comparison when leaving the Pokemon route
    useEffect(() => {
        if (currentRoute !== "pokemon") {
            setIsComparing(false);
            setIsComparisonClosing(false);
        }
    }, [currentRoute]);

    const handleToggleComparison = () => {
        if (isComparing) {
            setIsComparisonClosing(true);
            setTimeout(() => {
                setIsComparing(false);
                setIsComparisonClosing(false);
            }, 400); // match slideOutComparison duration
        } else {
            setComparisonKey(k => k + 1);
            setIsComparing(true);
        }
    };

    const comparisonStartNumber = (primaryNumber % 1025) + 1;

    return (
        <div className={`appLayout ${currentRoute === "pokemon" ? "pokemonRoute" : "typeRoute"}`}>
            {currentRoute === "pokemon" ? (
                <div className={`pokedexRow ${isComparing ? 'comparing' : 'single'}`}>
                    <PokedexCard
                        id="primary"
                        initialNumber={initialNumber}
                        isPrimary={true}
                        onPrimaryColorChange={setCardColor}
                        onPrimaryNumberChange={setPrimaryNumber}
                    />

                    {isComparing && (
                        <PokedexCard
                            key={comparisonKey}
                            id="comparison"
                            initialNumber={comparisonStartNumber}
                            isPrimary={false}
                            isEntering={!isComparisonClosing}
                            isExiting={isComparisonClosing}
                            onClose={handleToggleComparison}
                        />
                    )}
                </div>
            ) : currentRoute === "evolution" ? (
                <EvolutionTreeView
                    number={primaryNumber}
                    onSelectPokemon={(id) => {
                        setInitialNumber(id);
                        setCurrentRoute("pokemon");
                    }}
                    onClose={() => setCurrentRoute("pokemon")}
                />
            ) : (
                <TypeExplorerView
                    type={selectedType}
                    cardColor={cardColor}
                    onSelectPokemon={(id, name) => {
                        setInitialNumber(id);
                        setCurrentRoute("pokemon");
                        window.history.pushState({}, "", `/${name}`);
                    }}
                />
            )}

            <Sidebar
                cardColor={cardColor}
                setCurrentRoute={setCurrentRoute}
                setSelectedType={setSelectedType}
                currentRoute={currentRoute}
                isComparing={isComparing}
                onToggleComparison={handleToggleComparison}
                primaryNumber={primaryNumber}
            />
        </div>
    );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);